/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import each from 'lodash/each';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import maxBy from 'lodash/maxBy';
import uniq from 'lodash/uniq';

import activitySummary from './Commentary/activitySummary.json';
import portfolioGrowth from './Commentary/portfolioGrowth.json';
import rateOfReturn from './Commentary/rateOfReturn.json';

export const ACTIVITY_SUMMARY_TYPE = 'activitySummary';
export const PORTFOLIO_GROWTH_TYPE = 'portfolioGrowth';
export const RATE_OF_RETURN_TYPE = 'rateOfReturn';

export const commentaryMap = {
  [ACTIVITY_SUMMARY_TYPE]: activitySummary,
  [PORTFOLIO_GROWTH_TYPE]: portfolioGrowth,
  [RATE_OF_RETURN_TYPE]: rateOfReturn
};

// --- Utilities ---

export const sampleSpace = {
  activitySummary: [
    ['X', '-'], // hasIncome
    ['X', '-'], // hasAdditions
    ['X', '-'], // hasDeductions
    ['P', 'N', '-'], // marketFluctuation
    ['U', 'D', '-'], // endingMarketValue
    ['I', 'A', 'D', 'M', '-'] // primary
  ],

  portfolioGrowth: [
    ['G', 'L', '-'], // marketValue
    ['X', '-'], // hasAdditions
    ['X', '-'], // hasDeductions
    ['U', 'D', '-'] // marketFluctuation
  ],

  rateOfReturn: [
    ['X', '-'], // consolidated
    ['U', 'D', '-'], // sinceInceptionAll
    ['M', 'C', 'B', 'A', 'W', 'R', '-'], // sinceInceptionHighest
    ['M', 'C', 'B', 'A', 'W', 'Z', 'R', '-'], // oneYearHighest
    ['U', 'D', '-'], // oneYearReturn
    ['X', '-'] // oneYearReturnGreater
  ]
};

export const combinations = (sample) => {
  let results = [];

  let comb = function (pos, currStr) {
    if (pos >= sample.length) {
      results.push(currStr);
      return;
    }
    each(sample[pos], function (val) {
      comb(pos + 1, currStr + val);
    });
    // TODO: what is the value of this?
  };

  comb(0, '');
  return results;
};

// --- Activity Summary ---

export const ruleLookupIndexAS = (inc, add, ded, mkt, end, primary) => {
  let _inc = inc ? 'X' : '-';
  let _add = add ? 'X' : '-';
  let _ded = ded ? 'X' : '-';
  let _mkt = '-';
  let _end = '-';
  if (mkt) {
    _mkt = mkt == 'none' ? '-' : mkt == 'up' ? 'P' : 'N';
  }
  if (end) {
    _end = end == 'none' ? '-' : end == 'up' ? 'U' : 'D';
  }
  let _primary;
  switch (primary) {
    case undefined:
    case null:
    case 'null':
    case '-':
      _primary = '-';
      break;
    case 'income':
      _primary = 'I';
      break;
    case 'additions':
      _primary = 'A';
      break;
    case 'deductions':
      _primary = 'D';
      break;
    case 'marketFluctuation':
      _primary = 'M';
      break;
    case 'netInvestment':
      _primary = 'N';
      break;
    case 'endingMarketValue':
      _primary = 'E';
      break;
    default:
      throw `Unidentified [${primary}] primary indicator`;
  }
  return `${_inc}${_add}${_ded}${_mkt}${_end}${_primary}`;
};

export const ruleLookupMapAS = (rules) => {
  let lookup = {};
  rules.forEach((rule) => {
    let index = ruleLookupIndexAS(
      rule.hasIncome,
      rule.hasAdditions,
      rule.hasDeductions,
      rule.marketFluctuation,
      rule.endingMarketValue,
      rule.primary
    );
    lookup[index] = {
      templateIds: rule.templateIds,
      scenarioId: rule.scenarioId
    };
  });
  return lookup;
};

export const ruleLookupIndexByValuesAS = (inc, add, ded, mkt, begin, end) => {
  let _inc = !!inc;
  let _add = !!add;
  let _ded = !!ded;
  let _mkt = mkt == 0 ? 'none' : mkt > 0 ? 'up' : 'down';
  let cmv = end - begin;
  let _end = cmv == 0 ? 'none' : cmv > 0 ? 'up' : 'down';
  let _primary = null;
  let absArr = [inc, add, ded, mkt].map((num) => Math.abs(num));
  let maxValue = Math.max.apply(Math, absArr);
  let maxValCount = absArr.reduce((n, num) => (n += num == maxValue), 0);
  if (maxValue != 0 && maxValCount == 1) {
    let index = absArr.findIndex((num) => num == maxValue);
    switch (index) {
      case 0:
        _primary = 'income';
        break;
      case 1:
        _primary = 'additions';
        break;
      case 2:
        _primary = 'deductions';
        break;
      case 3:
        _primary = 'marketFluctuation';
        break;
      default:
        throw 'Invalid Index. Something went wrong';
    }
  }
  return ruleLookupIndexAS(_inc, _add, _ded, _mkt, _end, _primary);
};

// --- Mapping Functions ---

const mapTrueFalse = (tf) => {
  if (tf && typeof tf === 'string') {
    return tf === 'true' ? 'X' : '-';
  }
  return tf ? 'X' : '-';
};

const mapUpDown = (ud) => {
  let _ud = '-';
  if (ud) {
    _ud = ud === 'up' ? 'U' : ud === 'down' ? 'D' : _ud;
  }
  return _ud;
};

const mapGreaterLess = (gl) => {
  let _gl = '-';
  if (gl) {
    _gl = gl === 'greater' ? 'G' : gl === 'less' ? 'L' : _gl;
  }
  return _gl;
};

const mapProductType = (pt) => {
  let _pt = '-';
  if (pt) {
    switch (pt.toLowerCase()) {
      case 'mf':
        _pt = 'M';
        break;
      case '529':
        _pt = 'C';
        break;
      case 'brk':
        _pt = 'B';
        break;
      case 'app':
        _pt = 'A';
        break;
      case 'wpr':
        _pt = 'W';
        break;
      case 'multi':
        _pt = 'Z';
        break;
      case 'ras':
        _pt = 'R';
        break;
    }
  }
  return _pt;
};

const mapProductClass = (pc) => {
  let _pc = null;
  if (pc) {
    switch (pc.toLowerCase()) {
      case 'mutualfund':
        _pc = 'mf';
        break;
      case 'collegesavings':
        _pc = '529';
        break;
      case 'brokerage':
        _pc = 'brk';
        break;
      case 'activeplus':
        _pc = 'app';
        break;
      case 'workplace':
        _pc = 'wpr';
        break;
      case 'activeadvisory':
        _pc = 'ras';
        break;
    }
  }
  return _pc;
};

// --- Portfolio Growth ---

export const ruleLookupIndexPGT = (mv, add, ded, mf) => {
  let _mv = mapGreaterLess(mv);
  let _add = mapTrueFalse(add);
  let _ded = mapTrueFalse(ded);
  let _mf = mapUpDown(mf);
  return `${_mv}${_add}${_ded}${_mf}`;
};

export const ruleLookupMapPGT = (rules) => {
  let lookup = {};
  rules.forEach((rule) => {
    let index = ruleLookupIndexPGT(
      rule.marketValue,
      rule.hasAdditions,
      rule.hasDeductions,
      rule.marketFluctuation
    );
    lookup[index] = {
      templateIds: rule.templateIds,
      scenarioId: rule.scenarioId
    };
  });
  return lookup;
};

export const ruleLookupIndexByValuesPGT = (
  marketValueChange,
  netInvestment,
  numOfAdditions,
  numOfDeductions
) => {
  const marketFluctuation = marketValueChange > 0 ? 'up' : 'down';
  const marketVsInvestment =
    marketValueChange > netInvestment ? 'greater' : 'less';
  const hasAdditions = numOfAdditions > 0;
  const hasDeductions = numOfDeductions > 0;
  return ruleLookupIndexPGT(
    marketVsInvestment,
    hasAdditions,
    hasDeductions,
    marketFluctuation
  );
};

// --- Rate Of Return ---

export const ruleLookupIndexPRR = (
  consolidated,
  sinceInceptionAll,
  sinceInceptionHighest,
  oneYearHighest,
  oneYearReturn,
  oneYearReturnGreater
) => {
  const _consolidated = mapTrueFalse(consolidated);
  const _sinceInceptionAll = mapUpDown(sinceInceptionAll);
  const _sinceInceptionHighest = mapProductType(sinceInceptionHighest);
  const _oneYearHighest = mapProductType(oneYearHighest);
  const _oneYearReturn = mapUpDown(oneYearReturn);
  const _oneYearReturnGreater = mapTrueFalse(oneYearReturnGreater);
  return `${_consolidated}${_sinceInceptionAll}${_sinceInceptionHighest}${_oneYearHighest}${_oneYearReturn}${_oneYearReturnGreater}`;
};

export const ruleLookupMapPRR = (rules) => {
  let lookup = {};
  rules.forEach((rule) => {
    let index = ruleLookupIndexPRR(
      rule.consolidated,
      rule.sinceInceptionAll,
      rule.sinceInceptionHighest,
      rule.oneYearHighest,
      rule.oneYearReturn,
      rule.oneYearReturnGreater
    );
    lookup[index] = {
      templateIds: rule.templateIds,
      scenarioId: rule.scenarioId
    };
  });
  return lookup;
};

export const ruleLookupIndexByValuesPRR = (
  consolidated,
  consolidatedPerformance,
  productPerformance
) => {
  let _sinceInceptionAll = null;
  let _sinceInceptionHighest = null;
  let _oneYearHighest = null;
  let _oneYearReturn = null;
  let _oneYearReturnGreater = null;

  if (!consolidated) {
    if (isEmpty(productPerformance)) {
      return null;
    }
    // sinceInceptionAll
    const productSinceInception = uniq(
      map(productPerformance, (pp) => {
        return pp && pp.inceptionReturn > 0 ? 'up' : 'down';
      })
    );
    _sinceInceptionAll = null;
    if (productSinceInception.length === 2) {
      _sinceInceptionAll = 'mixed';
    } else if (productSinceInception.length === 1) {
      _sinceInceptionAll = productSinceInception[0];
    }
    // sinceInceptionHighest
    const maxSinceInception = maxBy(productPerformance, 'inceptionReturn');
    if (maxSinceInception) {
      _sinceInceptionHighest = mapProductClass(maxSinceInception.productClass);
      const maxOneYear = filter(productPerformance, (pp) => {
        if (pp && pp.inceptionReturn && pp.oneYearReturn) {
          return pp.oneYearReturn > pp.inceptionReturn;
        }
      });
      // oneYearHighest
      if (maxOneYear.length === 1) {
        _oneYearHighest = mapProductClass(maxOneYear[0].productClass);
      } else if (maxOneYear.length > 1) {
        _oneYearHighest = 'multi';
      }
    }
  } else {
    if (isEmpty(consolidatedPerformance)) {
      return null;
    }
    // sinceInceptionAll
    if (consolidatedPerformance.inceptionReturn) {
      _sinceInceptionAll =
        consolidatedPerformance.inceptionReturn > 0 ? 'up' : 'down';
    }
    // oneYearReturn
    _oneYearReturn = consolidatedPerformance.oneYearReturn > 0 ? 'up' : 'down';
    // oneYearReturnGreater
    _oneYearReturnGreater =
      consolidatedPerformance.oneYearReturn >
      consolidatedPerformance.inceptionReturn;
  }
  return ruleLookupIndexPRR(
    consolidated,
    _sinceInceptionAll,
    _sinceInceptionHighest,
    _oneYearHighest,
    _oneYearReturn,
    _oneYearReturnGreater
  );
};
